import { Paper, TableCell, TableRow, Typography, useMediaQuery, Theme, Stack } from "@mui/material"
import React, { useEffect } from "react"

// import { EntityBlock } from "../../components/EntityBlock"
import { type TokenHolder, getTokenHolders } from "../../api/token-api"
import { InMemoryTable } from "../../components/InMemoryTable"
import { LoadingSkeletons } from "../../components/LoadingSkeletons"
import { NumberFont } from "../../components/RootLayout/fonts"
import { TokenAmountBlock } from "../../components/TokenAmountBlock"
import { useTokenPrices } from "@/api/portfolio-api"
import { EntityBlock } from "@/components/EntityBlock"
import { UsdAmountBlock } from "@/components/UsdAmountBlock"
import { usePool } from "@/context/PoolContext"
import { useTokenInfo } from "@/hooks/use-token-info"

export function TokenHolderTable() {
  const { pool, baseToken } = usePool()
  const tokenId = baseToken

  const [prices] = useTokenPrices()

  const [data, setData] = React.useState<TokenHolder[]>([])
  const [circulatingSupply, setCirculatingSupply] = React.useState(0)
  const [loading, setLoading] = React.useState(true)

  const [tokenInfo] = useTokenInfo(tokenId)

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"))

  useEffect(() => {
    setData([])
  }, [tokenId])

  useEffect(() => {
    if (!tokenInfo) return

    setLoading(true)
    getTokenHolders(tokenInfo)
      .then((data) => {
        const sum = data.reduce((acc, holder) => acc + holder.balance, 0)
        setCirculatingSupply(sum)
        console.log("onTokenHolders", tokenInfo.ticker, data)
        setData(data)
      })
      .finally(() => setLoading(false))
  }, [tokenInfo])

  const tokenPrice = prices?.[tokenId]?.price

  if (loading || !prices || !tokenPrice) {
    return <LoadingSkeletons />
  }

  if (isMobile) {
    return (
      <>
        {loading && <LoadingSkeletons />}
        {!loading &&
          data.map((tokenHolder) => {
            const percent = ((tokenHolder.balance / circulatingSupply) * 100).toFixed(2)
            const value = tokenPrice * tokenHolder.balance

            return (
              <TableRow key={tokenHolder.entityId} hover>
                <TableCell colSpan={99}>
                  <Stack gap={1}>
                    <Stack direction="row" gap={1} alignItems="baseline">
                      <Typography fontFamily={NumberFont}>Rank: {tokenHolder.rank}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {percent}%
                      </Typography>
                    </Stack>

                    <Stack direction="row" gap={1}>
                      <Typography variant="body2">Holder:</Typography>
                      <EntityBlock
                        entityId={tokenHolder.entityId}
                        fullId
                        baseToken={pool?.baseToken as string}
                        quoteToken={pool?.quoteToken as string}
                        poolId={pool?.id as string}
                      />
                    </Stack>

                    <Stack direction="row" gap={4}>
                      <Typography variant="body2">
                        Balance:{" "}
                        <TokenAmountBlock
                          amount={tokenHolder.balance}
                          tokenId={tokenId}
                          hideCopyToClipboard
                          showTicker
                        />
                      </Typography>
                      <Typography variant="body2">
                        Value: <UsdAmountBlock amount={value} />
                      </Typography>
                    </Stack>
                  </Stack>
                </TableCell>
              </TableRow>
            )
          })}
      </>
    )
  }

  return (
    <Paper sx={{ overflow: "auto", background: "var(--mui-palette-paperAlt)" }}>
      <InMemoryTable
        loading={loading}
        initialSortField="balance"
        initialSortDir="desc"
        data={data}
        headerCells={[
          { label: "Rank", align: "center", sx: { width: 60 } },
          { label: "Holder" },
          { label: "%", align: "right" },
          {
            field: "rank",
            label: "Balance",
            align: "right",
            sortable: true,
          },
          { label: "Value", align: "right" },
        ]}
        renderRow={(tokenHolder: TokenHolder) => (
          <TableRow key={tokenHolder.entityId}>
            <TableCell
              align="center"
              variant="head"
              sx={{ borderBottom: "1px solid var(--mui-palette-TableCell-border)" }}
            >
              <Typography fontFamily={NumberFont} variant="inherit">
                {tokenHolder.rank}
              </Typography>
            </TableCell>
            <TableCell variant="text">
              <EntityBlock
                entityId={tokenHolder.entityId}
                fullId
                baseToken={pool?.baseToken as string}
                quoteToken={pool?.quoteToken as string}
                poolId={pool?.id as string}
              />
            </TableCell>
            <TableCell align="right" variant="number">
              {((tokenHolder.balance / circulatingSupply) * 100).toFixed(2)}%
            </TableCell>
            <TableCell align="right" variant="number">
              <TokenAmountBlock
                amount={tokenHolder.balance}
                tokenId={tokenId}
                hideCopyToClipboard
                showTicker
              />
            </TableCell>
            <TableCell align="right" variant="number">
              <UsdAmountBlock amount={tokenPrice * tokenHolder.balance} />
            </TableCell>
          </TableRow>
        )}
      />
    </Paper>
  )
}
